import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ReactComponent as ChevronIcon } from '$/assets/icons/Chevron.svg';

interface ReceivedClientsCellRendererProps {
  value?: any;
}

const ReceivedClientsCellRenderer: React.FC<ReceivedClientsCellRendererProps> = ({ value }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updatePosition = () => {
      if (triggerRef.current && popupRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        const popupHeight = popupRef.current.offsetHeight;
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;

        const shouldPositionAbove = spaceBelow < popupHeight && spaceAbove > spaceBelow;

        setPopupPosition({
          top: shouldPositionAbove ? rect.top - popupHeight : rect.bottom,
          left: rect.left,
        });
      }
    };

    if (isExpanded) {
      setTimeout(updatePosition, 0);
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [isExpanded]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        triggerRef.current &&
        popupRef?.current &&
        !triggerRef.current.contains(event.target as Node) &&
        !popupRef.current?.contains(event.target as Node)
      )
        setIsExpanded(false);
    }

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (!value?.length) return null;

  if (value.length <= 1) {
    return (
      <div className="workflow-management-clients">
        {value.map((client: { id: string; name: string }) => {
          return (
            <span key={`recieved-client-${client.id}`} className="workflow-management-recieved-client">
              {client.name}
            </span>
          );
        })}
      </div>
    );
  }

  const popupMenu = isExpanded ? (
    <div
      ref={popupRef}
      className="workflow-management-popup-menu"
      style={{ top: `${popupPosition.top}px`, left: `${popupPosition.left}px` }}>
      <div className="workflow-management-popup-header">Received Clients</div>
      <div className="workflow-management-popup-content">
        {value.map((client: { id: string; name: string }) => {
          return (
            <span key={`recieved-client-${client.id}`} className="workflow-management-recieved-client">
              {client.name}
            </span>
          );
        })}
      </div>
    </div>
  ) : null;

  return (
    <>
      <div className="workflow-management-dropdown-wrapper">
        <div
          ref={triggerRef}
          className="workflow-management-dropdown-trigger"
          onClick={() => setIsExpanded(!isExpanded)}>
          <span className="workflow-management-recieved-client workflow-management-dropdown-pill">
            {value.length} clients
            <ChevronIcon className={isExpanded ? 'chevron-up' : 'chevron-down'} />
          </span>
        </div>
      </div>
      {popupMenu && createPortal(popupMenu, document.body)}
    </>
  );
};

export default ReceivedClientsCellRenderer;
